// 📁 src/pages/MajorArcana.js
import React from "react";
import { Link } from "react-router-dom";
import Seo from "../components/seo";
import cardData from "../components/card-data";
import { cardPath } from "../utils/cardRoutes";
import { COMMON_COPY_ES, isSpanishLocale, localizedPath, translateCardName, translateKeywordPhrase, translateSuitName } from "../utils/i18n";

/* =========================================================
   THEME TOKENS (match TarotCards.js)
========================================================= */
const WRAP = "px-[1vw]";
const MAXW = "max-w-10xl mx-auto";
const FONT = "font-[Cormorant_Garamond]";
const TEXT = "text-white/90";
const BORDER_SOFT = "border-[#005C83]/12";
const PANEL_BASE =
  "relative overflow-hidden rounded-md " +
  BORDER_SOFT +
  " shadow-[0_2px_4px_rgba(0,0,0,0.35)]";

/* Canvas background */
const CANVAS_BG = `
  radial-gradient(1200px 900px at 50% -12%, rgba(3,121,155,0.32), rgba(3,121,155,0) 40%),
  linear-gradient(180deg, #014C75 0%, #005C83 22%, #016280ff 38%, #0C4B84 72%, #014C75 90%, #821E3C 100%)
`;

/* Silk panel */
const SILK_TURQ =
  "bg-[linear-gradient(135deg,rgba(12,63,107,0.4),rgba(20,33,61,0.88))] " +
  "before:absolute before:inset-0 before:bg-[radial-gradient(80%_60%_at_80%_0%,rgba(59,198,214,0.25),transparent_100%)] before:content-['']";

/* =========================================================
   SEO (About.js-style via <Seo /> component)
========================================================= */
const siteUrl = "https://tarotfate.com";
const pageUrl = `${siteUrl}/tarot/major-arcana`;
const ogImage = `${siteUrl}/icons/og-cover.jpg`;      // social share image
const logoWebp = `${siteUrl}/icons/logo.webp`;        // canonical logo

const metaDescription =
  "Learn the Major Arcana in depth. See images and meanings for all twenty two archetypes from The Fool to The World, with guidance for purpose, change and growth.";

const pageKeywords = `
  tarot, major arcana, major arcana meanings, tarot archetypes, tarot guide, tarot symbolism,
  upright meanings, reversed meanings, learn tarot, tarot study, tarot interpretation,
  the fool, the magician, the high priestess, the empress, the emperor, the hierophant, the lovers,
  the chariot, strength, the hermit, wheel of fortune, justice, the hanged man, death, temperance,
  the devil, the tower, the star, the moon, the sun, judgement, the world,
  tarot cards list, spiritual growth, life lessons, beginner tarot, advanced tarot,
  tarot in English, tarot en Español, tarot em Português, tarot en Français,
  tarot Deutsch, tarocchi Italiano, tarot Türkçe, tarot 日本語, tarot 한국어
`.replace(/\s+/g, " ").trim();

/* =========================================================
   Data: exact 22 slugs for the Major Arcana
========================================================= */
const majorArcanaSlugs = [
  "the-fool-tarot-card-reading-meaning",
  "the-magician-tarot-card-reading-meaning",
  "the-high-priestess-tarot-card-reading-meaning",
  "the-empress-tarot-card-reading-meaning",
  "the-emperor-tarot-card-reading-meaning",
  "the-hierophant-tarot-card-reading-meaning",
  "the-lovers-tarot-card-reading-meaning",
  "the-chariot-tarot-card-reading-meaning",
  "strength-tarot-card-reading-meaning",
  "the-hermit-tarot-card-reading-meaning",
  "wheel-of-fortune-tarot-card-reading-meaning",
  "justice-tarot-card-reading-meaning",
  "the-hanged-man-tarot-card-reading-meaning",
  "death-tarot-card-reading-meaning",
  "temperance-tarot-card-reading-meaning",
  "the-devil-tarot-card-reading-meaning",
  "the-tower-tarot-card-reading-meaning",
  "the-star-tarot-card-reading-meaning",
  "the-moon-tarot-card-reading-meaning",
  "the-sun-tarot-card-reading-meaning",
  "judgement-tarot-card-reading-meaning",
  "the-world-tarot-card-reading-meaning",
];

const majorCards = Object.values(cardData).filter((c) =>
  majorArcanaSlugs.includes(c.slug)
);

/* =========================================================
   JSON-LD (CollectionPage + ItemList + FAQPage)
========================================================= */
function schemaGraphFor(items, faqEntities) {
  const itemListElement = items.map((c, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: c.name,
    url: `${siteUrl}${cardPath(c)}`,
  }));

  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": `${siteUrl}#org`,
        name: "TarotFate",
        url: siteUrl,
        logo: { "@type": "ImageObject", url: logoWebp, width: 512, height: 512 },
      },
      {
        "@type": "WebSite",
        "@id": `${siteUrl}#website`,
        url: siteUrl,
        name: "TarotFate",
        publisher: { "@id": `${siteUrl}#org` },
        inLanguage: "en",
      },
      {
        "@type": "CollectionPage",
        "@id": `${pageUrl}#webpage`,
        url: pageUrl,
        name: "Major Arcana Tarot Cards | Meanings and Symbols | TarotFate",
        description: metaDescription,
        isPartOf: { "@id": `${siteUrl}#website` },
        primaryImageOfPage: { "@id": `${pageUrl}#image` },
        breadcrumb: { "@id": `${pageUrl}#breadcrumbs` },
        inLanguage: "en",
      },
      {
        "@type": "ImageObject",
        "@id": `${pageUrl}#image`,
        url: ogImage,
        width: 1200,
        height: 630,
        caption: "TarotFate cover",
      },
      {
        "@type": "BreadcrumbList",
        "@id": `${pageUrl}#breadcrumbs`,
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: siteUrl },
          { "@type": "ListItem", position: 2, name: "Tarot Cards", item: `${siteUrl}/tarot-cards` },
          { "@type": "ListItem", position: 3, name: "Major Arcana", item: pageUrl },
        ],
      },
      {
        "@type": "ItemList",
        "@id": `${pageUrl}#major-arcana`,
        name: "Major Arcana",
        itemListElement,
      },
      {
        "@type": "FAQPage",
        "@id": `${pageUrl}#faq`,
        mainEntity: faqEntities,
      },
    ],
  };
}

/* =========================================================
   Copy blocks
========================================================= */
const suitIntro =
  "The Major Arcana consists of twenty-two cards that trace the soul's journey from The Fool's first step to the wholeness of The World. They represent life's archetypal lessons such as awakening, choice, transformation and purpose. When these cards appear, they point to themes of identity and direction rather than passing events. Each symbol, color and gesture offers guidance, inviting you to reflect on where you stand and what is evolving. To work with them is to engage both light and shadow, gaining clarity for the next step on your path.";
const heroExplainer = "";

export default function MajorArcana() {
  const isSpanish = isSpanishLocale();
  const faq = isSpanish
    ? [
        {
          q: "Que son los Arcanos Mayores en el tarot?",
          a: "Los Arcanos Mayores son veintidos cartas arquetipicas. Describen aprendizajes importantes que forman identidad, proposito y direccion. En una lectura suelen indicar temas profundos, no solo eventos pasajeros.",
        },
        {
          q: "Cuantas cartas de Arcanos Mayores hay?",
          a: "Hay veintidos Arcanos Mayores, desde El Loco hasta El Mundo.",
        },
        {
          q: "Que significa que aparezcan muchos Arcanos Mayores?",
          a: "Suele indicar un periodo de aprendizaje importante. La tirada puede hablar de decisiones duraderas, cambios internos o temas que afectan mas de un area de la vida.",
        },
        {
          q: "Como puedo estudiar los Arcanos Mayores?",
          a: "Trabaja con pocas cartas a la vez. Observa simbolos, colores y sensaciones, luego compara con el significado de la carta. Con el tiempo veras patrones en todo el conjunto.",
        },
        {
          q: "Debo leer significados invertidos?",
          a: "Usa las inversiones si te ayudan a ver matices. Si prefieres, lee al derecho y usa la posicion o la pregunta para dar contexto. Lo importante es ser consistente.",
        },
        {
          q: "Los principiantes pueden empezar con Arcanos Mayores?",
          a: "Si. Muchas personas empiezan aqui porque las imagenes son memorables y las lecciones son claras. Luego puedes sumar los palos menores para contexto cotidiano.",
        },
      ]
    : [
        { q: "What are the Major Arcana in tarot?", a: "The Major Arcana is a group of twenty two archetypal cards. They describe key lessons that shape identity and purpose. In a reading they often signal change that affects direction rather than only short term events." },
        { q: "How many Major Arcana cards are in a deck?", a: "There are twenty two Major Arcana cards, beginning with The Fool and concluding with The World." },
        { q: "What does it mean when many Major Arcana appear in one spread?", a: "A spread with many Major Arcana suggests a period of significant learning. It points to themes that influence more than one area of life and may indicate lasting decisions." },
        { q: "How can I study the Major Arcana effectively?", a: "Work with a few cards at a time. Note the symbols and the feeling that each image evokes. Then read the card page and compare with your own notes. Over time you will see patterns across the set." },
        { q: "Should I read reversed meanings for the Major Arcana?", a: "Use reversals if they help you see nuance. If you prefer, read upright meanings and rely on position and question for context. Choose one method and apply it with consistency." },
        { q: "Are tarot meanings a substitute for professional advice?", a: "No. Tarot supports reflection and personal growth. It does not replace medical, legal or financial guidance. Please consult a qualified professional when needed." },
        { q: "Can beginners learn the Major Arcana first?", a: "Yes. Many readers begin with the Major Arcana because the images are memorable and the lessons are clear. Once you are comfortable, add the Minor Arcana suits for everyday context." },
        { q: "Which spreads work well with the Major Arcana?", a: "A single card pull supports daily study. A three card sequence such as past, present and future provides simple structure. The Celtic Cross adds depth when you want a broad view." },
      ];
  const faqEntities = faq.map(({ q, a }) => ({
    "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a },
  }));
  const schemaGraph = schemaGraphFor(majorCards, faqEntities);
  const title = isSpanish ? "Arcanos Mayores del Tarot | Significados y Símbolos | TarotFate" : "Major Arcana Tarot Cards | Meanings and Symbols | TarotFate";
  const intro = isSpanish
    ? "Los Arcanos Mayores recorren veintidós arquetipos del viaje interior, desde el primer impulso de El Loco hasta la integración de El Mundo. Hablan de propósito, cambios importantes, elección, despertar y crecimiento espiritual."
    : suitIntro;

  return (
    <div className={`${FONT} text-white min-h-screen relative overflow-hidden`} style={{ background: CANVAS_BG }}>
      {/* SEO (centralized, like About.js) */}
      <Seo
        title={title}
        description={isSpanish ? "Explora los Arcanos Mayores del tarot con imágenes, símbolos y significados claros para lecturas al derecho e invertidas." : metaDescription}
        url={`${siteUrl}${localizedPath("/tarot/major-arcana")}`}
        image={ogImage}
        schema={schemaGraph}
        keywords={pageKeywords}
        includeMega={true}
      />

      {/* Ambient auras */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-1/3 left-1/2 -translate-x-1/2 w-[1200px] h-[1200px] rounded-full opacity-25 blur-[160px] bg-[radial-gradient(circle,rgba(59,198,214,0.55)_0%,transparent_70%)]" />
        <div className="absolute -bottom-[25%] right-[5%] w-[700px] h-[700px] rounded-full opacity-20 blur-[140px] bg-[radial-gradient(circle,rgba(140,59,90,0.55)_0%,transparent_70%)]" />
      </div>

      {/* HERO */}
      <section className={`pt-6 ${WRAP} ${MAXW}`}>
        <h1 className="text-xl sm:text-2xl tracking-wide text-center font-bold">{isSpanish ? "Arcanos Mayores del Tarot" : "Major Arcana Tarot Cards"}</h1>
        <div className="flex items-center justify-center mb-1">
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#cbb7ea]/70 to-transparent"></div>
          <span className="px-4 text-[#f3eaff]/100">✦</span>
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#cbb7ea]/70 to-transparent"></div>
        </div>
        <p className={`${TEXT} text-base mt-2`}>{intro}</p>
        <p className={`${TEXT} text-[15px] md:text-[16px] leading-relaxed mt-2`}>{heroExplainer}</p>
      </section>

      {/* SINGLE SUIT PANEL */}
      <section className={`mt-6 ${WRAP} ${MAXW}`}>
        <div className={`${PANEL_BASE} ${SILK_TURQ} p-3 sm:p-6 md:p-6`}>
          <div className="relative z-[1]">
            <h2 className="text-base sm:text-lg font-semibold uppercase tracking-[0.12em] text-white/95">{translateSuitName("Major Arcana").toUpperCase()}</h2>
            <p className={`${TEXT} text-sm leading-relaxed mt-2`}>
              {isSpanish
                ? "Las cartas siguientes muestran el viaje completo. Abre cualquier imagen para estudiar significado, símbolos y guía para amor, trabajo y crecimiento personal."
                : "The cards below present the full journey. Open any image to study upright and reversed meanings, symbolism and guidance for love, career and personal development."}
            </p>

            <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4">
              {majorCards.map((card) => (
                <Link
                  key={card.slug}
                  to={localizedPath(cardPath(card))}
                  className={[
                    "group rounded-xl border border-white/15 bg-white/5 hover:bg-white/10 hover:border-white/25",
                    "transition-transform duration-150 hover:-translate-y-1",
                  ].join(" ")}
                >
                  <div className="p-2 flex flex-col items-center gap-1">
                    <img
                      src={card.image || `/card/${card.slug}.webp`}
                      alt={translateCardName(card.name)}
                      loading="lazy"
                      decoding="async"
                      className="h-[250px] w-full max-w-[240px] object-cover rounded-lg select-none pointer-events-none mx-auto"
                      draggable={false}
                      onContextMenu={(e) => e.preventDefault()}
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = "/icons/logo.webp"; // ✅ webp fallback
                      }}
                    />
                    <div className="text-center">
                      <h3 className="text-sm font-semibold text-white/95">{translateCardName(card.name)}</h3>
                      <p className="text-xs italic text-white/75">{translateKeywordPhrase(card.subtitle)}</p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
          <div className="absolute inset-x-0 top-0 h-px opacity-60 bg-gradient-to-r from-transparent via-white/40 to-transparent" />
        </div>
      </section>

      {/* FAQ (always open, colored Q/A) */}
<section id="faq" className={`mt-12 mb-16 ${WRAP} ${MAXW}`}>
  <div className="text-center">
    <h2 className="text-base sm:text-lg font-medium">{isSpanish ? COMMON_COPY_ES.faq : "Frequently Asked Questions"}</h2>
  </div>

  <div className="mt-6 rounded-sm border border-white/35 bg-white/5">
    <div className="divide-y divide-white/20">
      {faq.map(({ q, a }, i) => (
        <div key={i} className="px-4 md:px-6 py-4">
          {/* Question pill (on-theme) */}
          <p className="font-semibold text-sm sm:text-base leading-relaxed px-3 py-2 rounded-md text-white bg-white/10">
            {q}
          </p>
          {/* Answer */}
          <p className="mt-2 text-white/90 text-sm sm:text-base leading-relaxed px-3">
            {a}
          </p>
        </div>
      ))}
    </div>
  </div>

  {/* Explore other suits */}
  <div className="text-center mt-10">
    <div className="tf-outline-button-row">
      <Link
        to={localizedPath("/tarot/wands")}
        className="tf-outline-button"
      >
        {translateSuitName("Wands")}
      </Link>
      <Link
        to={localizedPath("/tarot/cups")}
        className="tf-outline-button"
      >
        {translateSuitName("Cups")}
      </Link>
      <Link
        to={localizedPath("/tarot/swords")}
        className="tf-outline-button"
      >
        {translateSuitName("Swords")}
      </Link>
      <Link
        to={localizedPath("/tarot/pentacles")}
        className="tf-outline-button"
      >
        {translateSuitName("Pentacles")}
      </Link>
      <Link
        to={localizedPath("/tarot-cards")}
        className="tf-outline-button"
      >
        {isSpanish ? COMMON_COPY_ES.allCards : "All Cards"}
      </Link>
    </div>
  </div>
</section>

    </div>
  );
}

